import { Injectable } from '@angular/core';
import { tokens, TokenKey } from './core/tokens.scss';
import { globaleStyles } from './core/globale.scss';
import { globaleComponentStyles } from './core/globalecomponent.scss';

/** Les 3 couleurs de base pilotables depuis Setting > Couleur */
export type BrandColorKey = 'colorPrimary' | 'colorAccentVariant' | 'colorAccentBase';

/** Valeurs par défaut des 3 couleurs de base (fallback tant qu'aucune config n'est chargée) */
const DEFAULT_BRAND_COLORS: Record<BrandColorKey, string> = {
  colorPrimary: tokens.colorPrimary,
  colorAccentVariant: tokens.colorAccentVariant,
  colorAccentBase: tokens.colorAccentTeal, // pilote aussi btnSelectedBg par défaut
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private rootStyleEl?: HTMLStyleElement;
  private derivationStyleEl?: HTMLStyleElement;
  private currentValues: Record<string, string> = { ...tokens } as any;
  private brandColors: Record<BrandColorKey, string> = { ...DEFAULT_BRAND_COLORS };

  /** Appelé une seule fois au démarrage (AppComponent) */
  init(): void {
    this.injectRootVariables();
    this.injectDerivationLayer();
    this.injectStaticBlock('app-globale-styles', globaleStyles);
    this.injectStaticBlock('app-globalecomponent-styles', globaleComponentStyles);
  }

  /** Couche 1 : tous les tokens bruts (valeurs de code, littérales) */
  private injectRootVariables(): void {
    if (!this.rootStyleEl) {
      this.rootStyleEl = document.createElement('style');
      this.rootStyleEl.id = 'app-tokens';
      document.head.appendChild(this.rootStyleEl);
    }
    let css = ':root {\n';
    for (const [key, value] of Object.entries(this.currentValues)) {
      css += `  --${key}: ${value};\n`;
    }
    css += '}\n';
    this.rootStyleEl.textContent = css;
  }

  /**
   * Couche 2 : injectée APRES app-tokens (donc prioritaire en cascade CSS,
   * même spécificité mais ordre de déclaration ultérieur = gagne).
   * Écrit les 3 couleurs de base + toutes leurs variantes en color-mix(),
   * qui référencent la base -> se recalculent seules dans le navigateur
   * dès que la base change, sans recalcul JS ni valeurs à stocker en plus.
   */
  private injectDerivationLayer(): void {
    if (!this.derivationStyleEl) {
      this.derivationStyleEl = document.createElement('style');
      this.derivationStyleEl.id = 'app-brand-derivation';
      document.head.appendChild(this.derivationStyleEl);
    }
    const p = 'colorPrimary';
    const s = 'colorAccentVariant';
    const a = 'colorAccentBase';

    this.derivationStyleEl.textContent = `
:root {
  /* ── Couleurs de base (personnalisables par l'admin) ── */
  --${p}: ${this.brandColors.colorPrimary};
  --${s}: ${this.brandColors.colorAccentVariant};
  --${a}: ${this.brandColors.colorAccentBase};

  /* ── Famille PRIMAIRE, dérivée de --colorPrimary ── */
  --colorPrimary50:      color-mix(in srgb, var(--${p}) 4%,  white);
  --colorPrimary100:     color-mix(in srgb, var(--${p}) 8%,  white);
  --colorPrimary200:     color-mix(in srgb, var(--${p}) 15%, white);
  --colorPrimary300:     color-mix(in srgb, var(--${p}) 30%, white);
  --colorPrimary400:     color-mix(in srgb, var(--${p}) 50%, white);
  --colorPrimary500:     var(--${p});
  --colorPrimary600:     color-mix(in srgb, var(--${p}) 88%, black);
  --colorPrimary700:     color-mix(in srgb, var(--${p}) 75%, black);
  --colorPrimary800:     color-mix(in srgb, var(--${p}) 60%, black);
  --colorPrimaryDark:    color-mix(in srgb, var(--${p}) 80%, black);
  --colorPrimaryLight:   color-mix(in srgb, var(--${p}) 10%, white);
  --colorPrimaryLight2:  color-mix(in srgb, var(--${p}) 8%,  white);
  --colorPrimarySurface: color-mix(in srgb, var(--${p}) 4%,  white);
  --colorPrimaryRing:    color-mix(in srgb, var(--${p}) 30%, white);
  --colorPrimarySoft:    color-mix(in srgb, var(--${p}) 7%,  white);
  --colorPrimaryHover:   color-mix(in srgb, var(--${p}) 6%,  white);

  /* ── Famille SECONDAIRE, dérivée de --colorAccentVariant ── */
  --colorAccentVariantDark:    color-mix(in srgb, var(--${s}) 80%, black);
  --colorAccentVariantLight:   color-mix(in srgb, var(--${s}) 15%, white);
  --colorAccentVariantSurface: color-mix(in srgb, var(--${s}) 4%,  white);

  /* ── Famille ACCENTUATION, dérivée de --colorAccentBase ── */
  --btnSelectedBg:        var(--${a});
  --colorAccentTeal:      var(--${a});
  --colorAccentTealDark:  color-mix(in srgb, var(--${a}) 80%, black);
  --colorAccentTealLight: color-mix(in srgb, var(--${a}) 20%, white);
  --colorAccentDark:      color-mix(in srgb, var(--${a}) 80%, black);
  --colorAccentSurface:   color-mix(in srgb, var(--${a}) 6%,  white);

  /* ── Sidebar (suit Accentuation, cf. correction S5bis) ── */
  --sbActiveBg:     color-mix(in srgb, var(--${a}) 8%,  white);
  --sbActiveBar:    var(--${a});
  --sbActiveIcon:   var(--${a});
  --sbActiveText:   color-mix(in srgb, var(--${a}) 80%, black);
  --sbBadgeBg:      var(--${a});
  --sbBadgeSoftBg:  color-mix(in srgb, var(--${a}) 8%,  white);
  --sbBadgeSoftFg:  color-mix(in srgb, var(--${a}) 80%, black);
}
`;
  }

  private injectStaticBlock(id: string, raw: string): void {
    if (document.getElementById(id)) return;
    const clean = raw.replace(/^\s*\/\/.*$/gm, '');
    const el = document.createElement('style');
    el.id = id;
    el.textContent = clean;
    document.head.appendChild(el);
  }

  /** Change UNE couleur de base à la volée (preview live depuis le formulaire) */
  setBrandColor(key: BrandColorKey, value: string): void {
    this.brandColors[key] = value;
    this.injectDerivationLayer();
  }

  /** Applique les 3 couleurs de base d'un coup (chargement COLOR_THEME depuis le backend) */
  applyBrandColors(overrides: Partial<Record<BrandColorKey, string>>): void {
    Object.assign(this.brandColors, overrides);
    this.injectDerivationLayer();
  }

  /** Reset aux valeurs de CODE (tokens.scss.ts) — distinct du défaut organisation */
  resetToCodeDefaults(): void {
    this.brandColors = { ...DEFAULT_BRAND_COLORS };
    this.injectDerivationLayer();
  }

  getBrandColors(): Record<BrandColorKey, string> {
    return { ...this.brandColors };
  }

  /** Conservé pour compat : usage générique token par token (hors 3 couleurs de base) */
  setToken(key: TokenKey, value: string): void {
    this.currentValues[key] = value;
    this.injectRootVariables();
  }
}